import React, { useMemo, useState } from 'react';
import { Download, RotateCcw } from 'lucide-react';
import { csvAmount, csvFileDate, downloadCsv, type CsvColumn } from '@/utils/csv';
import { usePlatformFees } from '../hooks/usePlatformFees';
import type { PlatformFeeItem, StripeDashboardMode } from '../api/stripeAdminApi';
import { FilterPopover } from './FilterPopover';
import { FormInput } from '../../../shared/FormInput';
import { FormSelect } from '../../../shared/FormSelect';

const euroEur = (n: number | null | undefined) =>
  n == null
    ? '—'
    : `${n.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} EUR`;

const fmtDate = (iso?: string | null) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

const PAGE_SIZE = 10;

const PERIOD_OPTIONS = [
  { value: '', label: 'Toutes' },
  { value: '7', label: '7 derniers jours' },
  { value: '30', label: '30 derniers jours' },
  { value: '90', label: '90 derniers jours' },
  { value: '365', label: '12 derniers mois' },
];
const TYPE_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'standard', label: 'Standard' },
  { value: 'upsell', label: 'Upsell' },
];
const STATUS_OPTIONS = [
  { value: '', label: 'Tous' },
  { value: 'invoiced', label: 'Facturé' },
  { value: 'pending', label: 'En attente' },
];
const labelOf = (opts: { value: string; label: string }[], v?: string) =>
  opts.find((o) => o.value === v)?.label;

/** Colonnes de l'export : celles du tableau, plus le paiement Stripe pour rapprocher. */
const EXPORT_COLUMNS: CsvColumn<PlatformFeeItem>[] = [
  { header: 'Date', value: (f) => fmtDate(f.date) },
  { header: 'Contrat', value: (f) => f.contractNum },
  { header: 'Échéance', value: (f) => f.echeanceNumber },
  { header: 'Type', value: (f) => (f.isUpsell ? 'Upsell' : 'Standard') },
  { header: 'Frais TTC (€)', value: (f) => csvAmount(f.amount) },
  { header: 'Statut', value: (f) => (f.invoiced ? 'Facturé' : 'En attente') },
  { header: 'Facture', value: (f) => f.invoiceLabel },
  { header: 'Paiement Stripe', value: (f) => f.chargeId },
];

const amountPill = (min?: string, max?: string) => {
  if (min && max) return `${min}–${max} €`;
  if (min) return `≥ ${min} €`;
  if (max) return `≤ ${max} €`;
  return null;
};

interface FeeFilters {
  search: string;
  datePreset: string;
  minAmount: string;
  maxAmount: string;
  type: string;
  status: string;
}
const DEFAULTS: FeeFilters = {
  search: '',
  datePreset: '',
  minAmount: '',
  maxAmount: '',
  type: '',
  status: '',
};

/**
 * Onglet « Frais de plateforme » : liste des frais de plateforme Affinicar (le
 * markup X% = application fee) facturés au vendeur, par paiement. Montant TTC.
 * Filtres en pastilles + popover façon Stripe (client-side). Le frais Stripe brut
 * n'apparaît jamais (payé par le compte plateforme).
 */
export const PlatformFeesTab: React.FC<{ societyId: string; mode?: StripeDashboardMode }> = ({
  societyId,
  mode = 'self',
}) => {
  const { data, isLoading, isError } = usePlatformFees(societyId, mode);

  const [committed, setCommitted] = useState<FeeFilters>(DEFAULTS);
  const [page, setPage] = useState(1);

  // Brouillons édités dans les popovers, committés au clic « Appliquer ».
  const [searchDraft, setSearchDraft] = useState('');
  const [periodDraft, setPeriodDraft] = useState('');
  const [minDraft, setMinDraft] = useState('');
  const [maxDraft, setMaxDraft] = useState('');
  const [typeDraft, setTypeDraft] = useState('');
  const [statusDraft, setStatusDraft] = useState('');

  const commit = (patch: Partial<FeeFilters>) => {
    setCommitted((c) => ({ ...c, ...patch }));
    setPage(1);
  };
  const resetAll = () => {
    setCommitted(DEFAULTS);
    setSearchDraft('');
    setPeriodDraft('');
    setMinDraft('');
    setMaxDraft('');
    setTypeDraft('');
    setStatusDraft('');
    setPage(1);
  };
  const activeCount = Object.values(committed).filter(Boolean).length;

  const all = useMemo(() => data ?? [], [data]);
  const filtered = useMemo(() => {
    let list = all;
    const q = committed.search.trim().toLowerCase();
    if (q) list = list.filter((f) => f.contractNum.toLowerCase().includes(q));
    if (committed.datePreset) {
      const days = Number(committed.datePreset);
      if (!Number.isNaN(days)) {
        const from = Date.now() - days * 86_400_000;
        list = list.filter((f) => new Date(f.date).getTime() >= from);
      }
    }
    if (committed.minAmount) {
      const m = Number(committed.minAmount);
      if (!Number.isNaN(m)) list = list.filter((f) => f.amount >= m);
    }
    if (committed.maxAmount) {
      const m = Number(committed.maxAmount);
      if (!Number.isNaN(m)) list = list.filter((f) => f.amount <= m);
    }
    if (committed.type) {
      list = list.filter((f) => (committed.type === 'upsell' ? f.isUpsell : !f.isUpsell));
    }
    if (committed.status) {
      list = list.filter((f) => (committed.status === 'invoiced' ? f.invoiced : !f.invoiced));
    }
    return list;
  }, [all, committed]);

  const total = filtered.length;
  const totalAmount = useMemo(() => filtered.reduce((s, f) => s + f.amount, 0), [filtered]);
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const from = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const to = Math.min(currentPage * PAGE_SIZE, total);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const canPrev = currentPage > 1;
  const canNext = currentPage < totalPages;

  const numberInputCls =
    'tw-w-full tw-border tw-border-gray-300 tw-rounded-lg tw-px-2 tw-py-1 tw-text-sm focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-affinicar-primary/20';
  const pagerBtnCls =
    'tw-px-3 tw-py-1.5 tw-text-sm tw-rounded-lg tw-border tw-border-gray-200 tw-text-gray-700 hover:tw-bg-gray-50 disabled:tw-opacity-40 disabled:tw-cursor-not-allowed';

  return (
    <div className="tw-bg-white">
      {/* Barre de filtres façon Stripe (pastilles + popovers) + total */}
      <div className="tw-p-3 tw-border-b tw-border-gray-200 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <FilterPopover
          label="Recherche"
          active={!!committed.search}
          pillValue={committed.search}
          onOpen={() => setSearchDraft(committed.search)}
          onApply={() => commit({ search: searchDraft })}
          onClear={() => commit({ search: '' })}
        >
          <FormInput
            size="sm"
            value={searchDraft}
            onChange={setSearchDraft}
            placeholder="N° de contrat..."
          />
        </FilterPopover>

        <FilterPopover
          label="Période"
          active={!!committed.datePreset}
          pillValue={labelOf(PERIOD_OPTIONS, committed.datePreset)}
          onOpen={() => setPeriodDraft(committed.datePreset)}
          onApply={() => commit({ datePreset: periodDraft })}
          onClear={() => commit({ datePreset: '' })}
        >
          <FormSelect size="sm" value={periodDraft} onChange={setPeriodDraft} options={PERIOD_OPTIONS} />
        </FilterPopover>

        <FilterPopover
          label="Montant"
          active={!!committed.minAmount || !!committed.maxAmount}
          pillValue={amountPill(committed.minAmount, committed.maxAmount)}
          onOpen={() => {
            setMinDraft(committed.minAmount);
            setMaxDraft(committed.maxAmount);
          }}
          onApply={() => commit({ minAmount: minDraft, maxAmount: maxDraft })}
          onClear={() => commit({ minAmount: '', maxAmount: '' })}
        >
          <div className="tw-flex tw-items-center tw-gap-2">
            <input
              type="number"
              value={minDraft}
              onChange={(e) => setMinDraft(e.target.value)}
              placeholder="Min €"
              className={numberInputCls}
            />
            <span className="tw-text-gray-400">—</span>
            <input
              type="number"
              value={maxDraft}
              onChange={(e) => setMaxDraft(e.target.value)}
              placeholder="Max €"
              className={numberInputCls}
            />
          </div>
        </FilterPopover>

        <FilterPopover
          label="Type"
          active={!!committed.type}
          pillValue={labelOf(TYPE_OPTIONS, committed.type)}
          onOpen={() => setTypeDraft(committed.type)}
          onApply={() => commit({ type: typeDraft })}
          onClear={() => commit({ type: '' })}
        >
          <FormSelect size="sm" value={typeDraft} onChange={setTypeDraft} options={TYPE_OPTIONS} />
        </FilterPopover>

        <FilterPopover
          label="Statut"
          active={!!committed.status}
          pillValue={labelOf(STATUS_OPTIONS, committed.status)}
          onOpen={() => setStatusDraft(committed.status)}
          onApply={() => commit({ status: statusDraft })}
          onClear={() => commit({ status: '' })}
        >
          <FormSelect size="sm" value={statusDraft} onChange={setStatusDraft} options={STATUS_OPTIONS} />
        </FilterPopover>

        {activeCount > 0 && (
          <button
            type="button"
            onClick={resetAll}
            className="tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-text-gray-500 hover:tw-text-gray-700"
          >
            <RotateCcw className="tw-w-4 tw-h-4" />
            Réinitialiser
          </button>
        )}

        <div className="tw-ml-auto tw-text-sm tw-text-gray-600">
          Total des frais&nbsp;:{' '}
          <span className="tw-font-semibold tw-text-gray-900 tw-tabular-nums">
            {euroEur(totalAmount)}
          </span>
        </div>
        <button
          type="button"
          onClick={() => downloadCsv(filtered, EXPORT_COLUMNS, `frais-plateforme-${csvFileDate()}.csv`)}
          disabled={filtered.length === 0}
          className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium tw-rounded-lg tw-bg-gray-100 tw-text-gray-800 hover:tw-bg-gray-200 disabled:tw-opacity-50 disabled:tw-cursor-not-allowed"
        >
          <Download className="tw-w-4 tw-h-4" />
          Exporter
        </button>
      </div>

      {/* Table */}
      <div className="tw-overflow-x-auto">
        <table className="tw-min-w-full">
          <thead>
            <tr className="tw-border-b tw-border-gray-200">
              <th className="tw-px-4 tw-py-3 tw-text-left tw-text-[13px] tw-font-medium tw-text-gray-500">
                Date
              </th>
              <th className="tw-px-4 tw-py-3 tw-text-left tw-text-[13px] tw-font-medium tw-text-gray-500">
                Contrat
              </th>
              <th className="tw-px-4 tw-py-3 tw-text-left tw-text-[13px] tw-font-medium tw-text-gray-500">
                Échéance
              </th>
              <th className="tw-px-4 tw-py-3 tw-text-left tw-text-[13px] tw-font-medium tw-text-gray-500">
                Type
              </th>
              <th className="tw-px-4 tw-py-3 tw-text-right tw-text-[13px] tw-font-medium tw-text-gray-500">
                Frais (TTC)
              </th>
              <th className="tw-px-4 tw-py-3 tw-text-left tw-text-[13px] tw-font-medium tw-text-gray-500">
                Statut
              </th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="tw-px-4 tw-py-10 tw-text-center tw-text-gray-500">
                  Chargement…
                </td>
              </tr>
            ) : isError ? (
              <tr>
                <td colSpan={6} className="tw-px-4 tw-py-10 tw-text-center tw-text-red-600">
                  Erreur de chargement des frais.
                </td>
              </tr>
            ) : pageItems.length === 0 ? (
              <tr>
                <td colSpan={6} className="tw-px-4 tw-py-10 tw-text-center tw-text-gray-500">
                  Aucun frais de plateforme.
                </td>
              </tr>
            ) : (
              pageItems.map((f) => (
                <tr
                  key={f.id}
                  className="tw-border-b tw-border-gray-100 last:tw-border-0 hover:tw-bg-gray-50"
                >
                  <td className="tw-px-4 tw-py-2.5 tw-text-sm tw-text-gray-700">{fmtDate(f.date)}</td>
                  <td className="tw-px-4 tw-py-2.5 tw-text-sm tw-text-gray-900">{f.contractNum}</td>
                  <td className="tw-px-4 tw-py-2.5 tw-text-sm tw-text-gray-700">
                    {f.echeanceNumber != null ? `N° ${f.echeanceNumber}` : '—'}
                  </td>
                  <td className="tw-px-4 tw-py-2.5">
                    <span className="tw-inline-block tw-px-2 tw-py-0.5 tw-rounded tw-bg-gray-100 tw-text-xs tw-text-gray-600">
                      {f.isUpsell ? 'Upsell' : 'Standard'}
                    </span>
                  </td>
                  <td className="tw-px-4 tw-py-2.5 tw-text-sm tw-text-right tw-tabular-nums tw-font-semibold tw-text-gray-900">
                    {euroEur(f.amount)}
                  </td>
                  <td className="tw-px-4 tw-py-2.5 tw-text-sm">
                    {f.invoiced ? (
                      <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-green-50 tw-text-green-700">
                        Facturé{f.invoiceLabel ? ` · ${f.invoiceLabel}` : ''}
                      </span>
                    ) : (
                      <span className="tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-amber-50 tw-text-amber-700">
                        En attente
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination « X-Y sur N » + Précédent / Suivant */}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 tw-border-t tw-border-gray-200">
        <span className="tw-text-sm tw-text-gray-500">
          {total === 0 ? '0 résultat' : `${from}-${to} sur ${total} résultats`}
        </span>
        <div className="tw-flex tw-items-center tw-gap-2">
          <button
            type="button"
            disabled={!canPrev}
            onClick={() => setPage(currentPage - 1)}
            className={pagerBtnCls}
          >
            Précédent
          </button>
          <button
            type="button"
            disabled={!canNext}
            onClick={() => setPage(currentPage + 1)}
            className={pagerBtnCls}
          >
            Suivant
          </button>
        </div>
      </div>
    </div>
  );
};
